/**
 * What the MECH and PILOT sheets' EDIT modes share (`decisions/0206` point 4): the capability an
 * editing host passes ({@link SheetEdit}), the EDIT / EDITING switch, the red `EDITING ·
 * AUTO-SAVED · UNDO · REDO · DONE` bar, and the image-upload pre-check. `MechSheetEdit.tsx` and
 * `PilotSheetEdit.tsx` build each sheet's controls on top.
 */
import { ALLOWED_UPLOAD_CONTENT_TYPES, MAX_UPLOAD_BYTES, type Result } from "@lancer-vtt/shared";
import type { RosterCommand, RosterUndoState } from "@lancer-vtt/domain";
import type { ReactNode } from "react";
import type { BuildSignal } from "../../lib/editor/buildAdvisor";
import type { SectionFlag } from "../../lib/encounter/unitLoadout";

/**
 * What an editing host grants a sheet: the roster dispatch, the subject's undo scope and the EDIT
 * switch. A host that passes `null` gets a sheet with no EDIT button and no edit control.
 */
export type SheetEdit = {
  dispatch: (cmd: RosterCommand) => Result<void>;
  actorId: string;
  /** EDIT is on (the page's `/mech/<id>/edit` or `/pilot/edit` path mode). */
  editing: boolean;
  onEditing: (on: boolean) => void;
  /** UNDO / REDO over the sheet's own subject (`mechScope` / `pilotScope`), never another's. */
  undo: () => void;
  redo: () => void;
  undoState: RosterUndoState;
};

export function EditToggle({ edit }: { edit: SheetEdit }) {
  return (
    <button type="button" className={`unit-sheet-action${edit.editing ? " is-on" : ""}`} aria-pressed={edit.editing} onClick={() => edit.onEditing(!edit.editing)}>
      {edit.editing ? "EDITING" : "EDIT"}
    </button>
  );
}

/** The red bar under the header while EDIT is on; `children` rides under it (PILOT's build line). */
export function EditBar({ edit, children }: { edit: SheetEdit; children?: ReactNode }) {
  return (
    <>
      <div className="unit-sheet-editbar" role="toolbar" aria-label="Editing">
        <span className="unit-sheet-editbar-label">EDITING · AUTO-SAVED</span>
        <button type="button" className="unit-sheet-mini" disabled={!edit.undoState.canUndo} title={edit.undoState.undoLabel ?? undefined} onClick={edit.undo}>
          UNDO
        </button>
        <button type="button" className="unit-sheet-mini" disabled={!edit.undoState.canRedo} title={edit.undoState.redoLabel ?? undefined} onClick={edit.redo}>
          REDO
        </button>
        <button type="button" className="unit-sheet-mini" onClick={() => edit.onEditing(false)}>
          DONE
        </button>
      </div>
      {children}
    </>
  );
}

/** A section head's flag from its `buildSignals`: `warn` if any is invalid, the reasons one per line. */
export function sectionFlag(signals: readonly BuildSignal[] | undefined): SectionFlag | undefined {
  if (!signals || signals.length === 0) return undefined;
  return {
    severity: signals.some((s) => s.severity === "warn") ? "warn" : "todo",
    title: signals.map((s) => `${s.severity === "warn" ? "⚠" : "▲"} ${s.message}`).join("\n"),
  };
}

/** The client pre-check on an image pick (UX only; the upload service is the gate). `null` when it may upload. */
export function imageRejection(file: File): string | null {
  if (!(ALLOWED_UPLOAD_CONTENT_TYPES as readonly string[]).includes(file.type)) return "Use a PNG, JPG, or WEBP image.";
  if (file.size > MAX_UPLOAD_BYTES) return "Image too large (max 5 MB).";
  return null;
}

/** The upload control: a label-button over a hidden file input. */
export function ImageUpload({ label, busy, hasImage, noun, onFile }: { label: string; busy: boolean; hasImage: boolean; noun: string; onFile: (file: File) => void }) {
  return (
    <label className="unit-sheet-mini unit-sheet-art-upload" title="PNG, JPG or WEBP, 5 MB max">
      <input
        type="file"
        accept={ALLOWED_UPLOAD_CONTENT_TYPES.join(",")}
        disabled={busy}
        aria-label={label}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = ""; // let the same file be re-picked after an error
          if (file) onFile(file);
        }}
      />
      {busy ? "UPLOADING…" : `${hasImage ? "REPLACE" : "UPLOAD"} ${noun}`}
    </label>
  );
}
